import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import PerformanceBadge from '../components/PerformanceBadge';
import {
  Activity, AlertTriangle, ArrowRight, ChevronRight, ClipboardList,
  Plus, Search, TrendingUp, Users,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_PILL = {
  Draft: 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800 dark:text-gray-300',
  Incomplete: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300',
  Complete: 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300',
};

const CAT_KEY_TO_LABEL = {
  excellent: 'Excellent',
  good: 'Good',
  standard: 'Standard',
  needs_improvement: 'Needs Improvement',
  unclassified: 'Unclassified',
};

const CAT_KEY_TO_COLOR_KEY = {
  excellent: 'green',
  good: 'blue',
  standard: 'amber',
  needs_improvement: 'red',
  unclassified: 'gray',
};

const CAT_KEY_TO_BAR = {
  excellent: 'bg-emerald-500',
  good: 'bg-blue-500',
  standard: 'bg-amber-500',
  needs_improvement: 'bg-rose-500',
  unclassified: 'bg-gray-400',
};

const SummaryCard = ({ label, value, helper, icon: Icon, tone = 'default', testId }) => {
  const tones = {
    default: 'text-gray-900 dark:text-gray-100',
    good: 'text-emerald-700 dark:text-emerald-300',
    warn: 'text-amber-700 dark:text-amber-300',
    bad: 'text-rose-700 dark:text-rose-300',
  };
  return (
    <Card data-testid={testId}>
      <CardContent className="pt-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
            <p className={`text-3xl font-bold mt-1 ${tones[tone]}`}>{value}</p>
            {helper && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{helper}</p>
            )}
          </div>
          {Icon && (
            <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-900">
              <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const FitnessDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [season, setSeason] = useState('any');
  const [squad, setSquad] = useState('any');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Football season convention: rolls over in July.
  const currentSeasonString = () => {
    const now = new Date();
    const y = now.getFullYear();
    return now.getMonth() + 1 >= 7 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
  };
  const previousSeasonString = () => {
    const [a, b] = currentSeasonString().split('-').map(Number);
    return `${a - 1}-${b - 1}`;
  };
  // Map a season string like "2025-2026" to its inclusive ISO bounds.
  const seasonBounds = (seasonStr) => {
    if (!seasonStr || !/^\d{4}-\d{4}$/.test(seasonStr)) return [null, null];
    const [a, b] = seasonStr.split('-');
    return [`${a}-07-01`, `${b}-06-30`];
  };
  const isoToday = () => new Date().toISOString().slice(0, 10);
  const isoDaysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
  };

  const presetActive = useMemo(() => {
    if (!startDate && !endDate) return null;
    if (endDate === isoToday() && startDate === isoDaysAgo(7)) return 'last7';
    if (endDate === isoToday() && startDate === isoDaysAgo(30)) return 'last30';
    const [cs, ce] = seasonBounds(currentSeasonString());
    if (startDate === cs && endDate === ce) return 'thisSeason';
    const [ps, pe] = seasonBounds(previousSeasonString());
    if (startDate === ps && endDate === pe) return 'lastSeason';
    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const applyPreset = (key) => {
    if (key === 'last7') {
      setStartDate(isoDaysAgo(7)); setEndDate(isoToday());
    } else if (key === 'last30') {
      setStartDate(isoDaysAgo(30)); setEndDate(isoToday());
    } else if (key === 'thisSeason') {
      const [s, e] = seasonBounds(currentSeasonString());
      setStartDate(s); setEndDate(e);
    } else if (key === 'lastSeason') {
      const [s, e] = seasonBounds(previousSeasonString());
      setStartDate(s); setEndDate(e);
    } else if (key === 'clear') {
      setStartDate(''); setEndDate('');
    }
  };

  useEffect(() => {
    if (!['Admin', 'Staff'].includes(user?.role)) {
      navigate('/dashboard');
      return;
    }
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season, squad, startDate, endDate]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const params = {};
      if (season !== 'any') params.season = season;
      if (squad !== 'any') params.squad = squad;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await axios.get(`${API}/assessments/dashboard`, { params });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const filteredSessions = useMemo(() => {
    const list = data?.latest_sessions || [];
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter(s => (s.session_name || '').toLowerCase().includes(q));
  }, [data, search]);

  const filteredMissing = useMemo(() => {
    const list = data?.missing_results || [];
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter(m =>
      (m.athlete_name || '').toLowerCase().includes(q) ||
      (m.session_name || '').toLowerCase().includes(q),
    );
  }, [data, search]);

  if (loading || !data) {
    return (
      <Layout>
        <div className="p-8 flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
        </div>
      </Layout>
    );
  }

  const { summary_cards: s, category_breakdown: cats } = data;
  const totalCatResults = Object.values(cats || {}).reduce((a, b) => a + b, 0);

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="fit-dashboard">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100"
                style={{ fontFamily: 'Manrope, sans-serif' }}>
              Fitness Assessment Dashboard
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Latest testing activity, focus areas and improvements at a glance.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate('/assessments')}
                    data-testid="qa-record-individual">
              Record Result
            </Button>
            <Button variant="outline" onClick={() => navigate('/assessments/bulk')}
                    data-testid="qa-bulk-list">
              Bulk Sessions
            </Button>
            {user?.role === 'Admin' && (
              <Button variant="outline" onClick={() => navigate('/assessments/standards')}
                      data-testid="qa-standards">
                Standards Review
              </Button>
            )}
            <Button className="bg-pfap-green hover:bg-pfap-green/90"
                    onClick={() => navigate('/assessments/bulk')}
                    data-testid="qa-create-bulk">
              <Plus className="w-4 h-4 mr-1" /> New Session
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-5 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <p className="text-xs text-gray-500 mb-1">Search session or athlete</p>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                <Input
                  className="pl-8"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Type a name…"
                  data-testid="dash-search"
                />
              </div>
            </div>
            <div className="w-44">
              <p className="text-xs text-gray-500 mb-1">Season</p>
              <Select value={season} onValueChange={setSeason}>
                <SelectTrigger data-testid="dash-filter-season"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">All seasons</SelectItem>
                  {(data.available_filters?.seasons || []).map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-44">
              <p className="text-xs text-gray-500 mb-1">Squad</p>
              <Select value={squad} onValueChange={setSquad}>
                <SelectTrigger data-testid="dash-filter-squad"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">All squads</SelectItem>
                  {(data.available_filters?.squads || []).map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <p className="text-xs text-gray-500 mb-1">From</p>
              <Input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                data-testid="dash-filter-start-date"
              />
            </div>
            <div className="w-40">
              <p className="text-xs text-gray-500 mb-1">To</p>
              <Input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                data-testid="dash-filter-end-date"
              />
            </div>
            {(startDate || endDate) && (
              <Button
                variant="ghost" size="sm"
                onClick={() => { setStartDate(''); setEndDate(''); }}
                data-testid="dash-filter-clear-dates"
              >
                Clear dates
              </Button>
            )}
            {/* Preset date chips */}
            <div className="flex flex-wrap gap-1.5 items-center w-full md:w-auto md:ml-auto"
                 data-testid="dash-date-presets">
              {[
                { key: 'last7', label: 'Last 7 days' },
                { key: 'last30', label: 'Last 30 days' },
                { key: 'thisSeason', label: 'This season' },
                { key: 'lastSeason', label: 'Last season' },
              ].map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => applyPreset(p.key)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition ${
                    presetActive === p.key
                      ? 'bg-pfap-green text-white border-pfap-green'
                      : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-pfap-green hover:text-pfap-green'
                  }`}
                  data-testid={`dash-preset-${p.key}`}
                  aria-pressed={presetActive === p.key}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6"
             data-testid="dash-summary-grid">
          <SummaryCard
            label="Total sessions" value={s.total_sessions}
            helper={s.current_season || 'All time'}
            icon={ClipboardList} testId="dash-card-total"
          />
          <SummaryCard
            label="Complete" value={s.complete_sessions}
            helper="Fully recorded" tone="good"
            icon={Activity} testId="dash-card-complete"
          />
          <SummaryCard
            label="Incomplete" value={s.incomplete_sessions}
            helper="Need finishing" tone="warn"
            icon={AlertTriangle} testId="dash-card-incomplete"
          />
          <SummaryCard
            label="Athletes assessed" value={s.athletes_assessed_this_season}
            helper={`This season · ${s.current_season || '—'}`}
            icon={Users} testId="dash-card-athletes"
          />
          <SummaryCard
            label="Missing results" value={s.missing_results}
            helper="Cells still empty" tone="warn"
            icon={ClipboardList} testId="dash-card-missing"
          />
          <SummaryCard
            label="Needs Improvement" value={s.category_counts?.needs_improvement || 0}
            helper="Across all results" tone="bad"
            icon={TrendingUp} testId="dash-card-needs-improvement"
          />
        </div>

        {/* Category breakdown */}
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Performance Category Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {totalCatResults === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm py-2"
                 data-testid="dash-cat-empty">
                No classified results yet.
              </p>
            ) : (
              <div className="space-y-2" data-testid="dash-category-breakdown">
                {Object.entries(cats || {}).map(([k, v]) => {
                  const pct = totalCatResults ? Math.round((v / totalCatResults) * 100) : 0;
                  return (
                    <div key={k} className="flex items-center gap-3 text-sm"
                         data-testid={`dash-cat-row-${k}`}>
                      <span className="w-40 text-gray-700 dark:text-gray-300">{CAT_KEY_TO_LABEL[k]}</span>
                      <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden h-3">
                        <div className={`h-full ${CAT_KEY_TO_BAR[k]}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-16 text-right text-gray-600 dark:text-gray-400 text-xs">
                        {v} · {pct}%
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Continue Incomplete */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Continue Incomplete Sessions</CardTitle>
            </CardHeader>
            <CardContent>
              {(data.incomplete_sessions || []).length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-sm py-3"
                   data-testid="dash-incomplete-empty">
                  All assessment sessions are complete.
                </p>
              ) : (
                <div className="space-y-2" data-testid="dash-incomplete-list">
                  {data.incomplete_sessions.map(s => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between gap-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900/40 cursor-pointer transition"
                      onClick={() => navigate(`/assessments/bulk/${s.id}`)}
                      data-testid={`dash-incomplete-row-${s.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                            {s.session_name}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full border text-[10px] ${STATUS_PILL[s.status] || STATUS_PILL.Draft}`}>
                            {s.status}
                          </span>
                          {s.squad && <Badge variant="outline" className="text-[10px]">{s.squad}</Badge>}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {s.assessment_date} · {s.athlete_count} athlete{s.athlete_count !== 1 && 's'}
                          {' · '}{s.missing_count} missing
                          {' · '}{s.completion_percent}% complete
                        </p>
                      </div>
                      <Button size="sm" variant="ghost"
                              data-testid={`dash-incomplete-continue-${s.id}`}>
                        Continue <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Latest sessions */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Latest Testing Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3"
                 data-testid="dash-latest-helper">
                Showing bulk assessment sessions. Individual single-athlete records remain available in <a className="underline hover:text-emerald-600" onClick={() => navigate('/assessments')}>Athlete Progress</a>.
              </p>
              {filteredSessions.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-sm py-3"
                   data-testid="dash-latest-empty">
                  No bulk sessions yet.
                </p>
              ) : (
                <div className="space-y-2" data-testid="dash-latest-list">
                  {filteredSessions.slice(0, 6).map(s => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between gap-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900/40 cursor-pointer"
                      onClick={() => navigate(`/assessments/bulk/${s.id}`)}
                      data-testid={`dash-latest-row-${s.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">{s.session_name}</span>
                          <span className={`px-2 py-0.5 rounded-full border text-[10px] ${STATUS_PILL[s.status] || STATUS_PILL.Draft}`}>
                            {s.status}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {s.assessment_date}
                          {s.squad ? ` · ${s.squad}` : ''}
                          {' · '}{s.athlete_count}×{s.test_count}
                          {' · '}{s.completion_percent}%
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Athletes Needing Attention */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Athletes Needing Attention</CardTitle>
            </CardHeader>
            <CardContent>
              {(data.athletes_needing_attention || []).length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-sm py-3"
                   data-testid="dash-attention-empty">
                  Nothing flagged. Everyone is on track.
                </p>
              ) : (
                <div className="space-y-2" data-testid="dash-attention-list">
                  {data.athletes_needing_attention.map(a => (
                    <div
                      key={a.athlete_id}
                      className="flex items-center justify-between p-3 rounded-lg border border-rose-100 dark:border-rose-900/40 bg-rose-50/40 dark:bg-rose-900/10"
                      data-testid={`dash-attention-row-${a.athlete_id}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{a.athlete_name}</span>
                          {a.squad && <Badge variant="outline" className="text-[10px]">{a.squad}</Badge>}
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          {a.needs_improvement_count} focus result{a.needs_improvement_count !== 1 && 's'}
                          {a.focus_area ? ` · Focus: ${a.focus_area}` : ''}
                          {a.latest_assessment_date ? ` · Last: ${a.latest_assessment_date}` : ''}
                        </p>
                      </div>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => navigate(`/assessments?athlete=${a.athlete_id}`)}
                        data-testid={`dash-attention-view-${a.athlete_id}`}
                      >
                        View Progress <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Most Improved */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Most Improved Athletes</CardTitle>
            </CardHeader>
            <CardContent>
              {(data.most_improved_athletes || []).length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-sm py-3"
                   data-testid="dash-improved-empty">
                  No comparisons yet — at least two assessments per test are needed.
                </p>
              ) : (
                <div className="space-y-2" data-testid="dash-improved-list">
                  {data.most_improved_athletes.map((m, i) => (
                    <div
                      key={`${m.athlete_id}-${m.test_type}-${i}`}
                      className="flex items-center justify-between p-3 rounded-lg border border-emerald-100 dark:border-emerald-900/40 bg-emerald-50/30 dark:bg-emerald-900/10"
                      data-testid={`dash-improved-row-${m.athlete_id}-${m.test_type}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{m.athlete_name}</span>
                          {m.squad && <Badge variant="outline" className="text-[10px]">{m.squad}</Badge>}
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          <strong>{m.test_type}:</strong> {m.previous_value}{m.unit} → {m.latest_value}{m.unit}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                            {m.improvement_text}
                          </span>
                          {m.category_movement && (
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                              {m.category_movement}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => navigate(`/assessments?athlete=${m.athlete_id}`)}
                        data-testid={`dash-improved-view-${m.athlete_id}`}
                      >
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Missing Results */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Missing Results Overview</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredMissing.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm py-3"
                 data-testid="dash-missing-empty">
                No missing results across recent sessions.
              </p>
            ) : (
              <div className="overflow-x-auto" data-testid="dash-missing-list">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                      <th className="py-2 pr-3">Athlete</th>
                      <th className="py-2 pr-3">Squad</th>
                      <th className="py-2 pr-3">Session</th>
                      <th className="py-2 pr-3">Date</th>
                      <th className="py-2 pr-3">Missing tests</th>
                      <th className="py-2 pr-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMissing.slice(0, 25).map((m, i) => (
                      <tr key={`${m.athlete_id}-${m.session_id}-${i}`}
                          className="border-b last:border-0 dark:border-gray-800"
                          data-testid={`dash-missing-row-${m.athlete_id}-${m.session_id}`}>
                        <td className="py-2 pr-3 font-medium">{m.athlete_name}</td>
                        <td className="py-2 pr-3 text-gray-500 dark:text-gray-400">{m.squad || '—'}</td>
                        <td className="py-2 pr-3 text-gray-700 dark:text-gray-300 truncate max-w-[200px]">{m.session_name}</td>
                        <td className="py-2 pr-3 text-gray-500 dark:text-gray-400">{m.assessment_date}</td>
                        <td className="py-2 pr-3">
                          <div className="flex flex-wrap gap-1">
                            {m.missing_tests.slice(0, 4).map(t => (
                              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                                {t}
                              </span>
                            ))}
                            {m.missing_tests.length > 4 && (
                              <span className="text-[10px] text-gray-500">+{m.missing_tests.length - 4} more</span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 pr-3">
                          <Button size="sm" variant="ghost"
                                  onClick={() => navigate(`/assessments/bulk/${m.session_id}`)}
                                  data-testid={`dash-missing-open-${m.session_id}`}>
                            Open
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hidden swatch — keeps PerformanceBadge bundled if we link to it later */}
        <div className="hidden">
          <PerformanceBadge label="Excellent" colorKey="green" />
        </div>
      </div>
    </Layout>
  );
};

export default FitnessDashboard;
